import React, { useState } from "react";
import { Checkbox, Dropdown, Input } from "antd";
import { DownOutlined, SearchOutlined } from "@ant-design/icons";
import DebounceInput from "./DebounceInput";

/**
 * CustomCheckboxDropdown — Reusable multi-select dropdown with search + Select All
 *
 * Props:
 *   options           {Array}    Required. [{ label: string, value: any }, ...]
 *   value             {Array}    Controlled selected values []
 *   onChange          {Function} Called with updated selected values array
 *   placeholder       {string}   Trigger placeholder
 *   className         {string}   Extra class on the trigger
 *   disabled          {boolean}  Disables the whole dropdown
 *   searchPlaceholder {string}   Placeholder inside the search input
 *   disabledValues    {Array}    Values that are always checked and cannot be toggled
 */
const CustomCheckboxDropdown = ({
  options = [],
  value = [],
  onChange,
  placeholder = "Select...",
  className = "",
  disabled = false,
  searchPlaceholder = "Search...",
  disabledValues = [],
  dropdownClassName = "",
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const filteredOptions = options.filter(opt =>
    String(opt.label).toLowerCase().includes(search.toLowerCase())
  );

  // Select All / indeterminate only considers non-disabled options
  const togglableOptions = options.filter(opt => !disabledValues.includes(opt.value));
  const allChecked =
    togglableOptions.length > 0 && togglableOptions.every(opt => value.includes(opt.value));
  const indeterminate = togglableOptions.some(opt => value.includes(opt.value)) && !allChecked;

  const handleSelectAll = () => {
    if (allChecked) {
      // Uncheck all togglable — keep disabled values in selection
      onChange(value.filter(v => disabledValues.includes(v)));
    } else {
      // Check all togglable (disabled already present in value)
      onChange([...new Set([...value, ...togglableOptions.map(o => o.value)])]);
    }
  };

  const handleOptionClick = optValue => {
    if (disabledValues.includes(optValue)) return;
    onChange(value.includes(optValue) ? value.filter(v => v !== optValue) : [...value, optValue]);
  };

  const handleOpenChange = isOpen => {
    if (!isOpen) setSearch("");
    setOpen(isOpen);
  };

  const dropdownContent = (
    // onMouseDown stopPropagation prevents Antd's document-level listener
    // from seeing clicks inside the panel — keeps the dropdown open on option select
    <div
      className="bg-white rounded-lg shadow-lg border min-h-72 min-w-[220px] overflow-auto border-gray-200"
      onMouseDown={e => e.stopPropagation()}
    >
      {/* Search */}
      <div className="p-2">
        <DebounceInput
          size="small"
          placeholder={searchPlaceholder}
          value={search}
          onChange={value => setSearch(value)}
          allowClear
          autoFocus
        />
      </div>

      {/* Select All — hidden while filtering */}
      {search === "" && togglableOptions.length > 0 && (
        <div className="flex items-center px-3 py-2 border-b border-gray-100 hover:bg-gray-50 cursor-pointer">
          <Checkbox
            indeterminate={indeterminate}
            checked={allChecked}
            onChange={handleSelectAll}
            className="w-full"
          >
            Select All
          </Checkbox>
        </div>
      )}

      {/* Options */}
      <div className="min-h-[220px] max-h-[300px] overflow-y-auto">
        {filteredOptions.length === 0 ? (
          <div className="px-3 py-4 text-center text-sm text-gray-400">No options found</div>
        ) : (
          filteredOptions.map(opt => {
            const isDisabled = disabledValues.includes(opt.value);
            return (
              <div
                key={opt.value}
                className={`flex items-center px-3 py-1.5 hover:bg-gray-50 ${
                  isDisabled ? "cursor-not-allowed" : "cursor-pointer"
                }`}
              >
                <Checkbox
                  checked={value.includes(opt.value)}
                  disabled={isDisabled}
                  onChange={() => handleOptionClick(opt.value)}
                  className="w-full"
                >
                  {opt.label}
                </Checkbox>
              </div>
            );
          })
        )}
      </div>
    </div>
  );

  return (
    <Dropdown
      open={open}
      onOpenChange={handleOpenChange}
      trigger={["click"]}
      disabled={disabled}
      className={dropdownClassName}
      dropdownRender={() => dropdownContent}
    >
      <div
        className={`flex items-center justify-between border-1 border-solid rounded-md px-3 bg-white cursor-pointer select-none transition-colors
          ${
            disabled ? "cursor-not-allowed bg-gray-100" : "hover:border-twLinkColor border-gray-300"
          }
          ${open ? "!border-blue-500" : ""}
          ${className}
        `}
        style={{ minHeight: 32, paddingTop: 4, paddingBottom: 4 }}
      >
        <span
          className={`truncate text-sm ${value.length > 0 ? "text-gray-800" : "text-gray-400"}`}
        >
          {value.length > 0 ? `${placeholder} (${value.length})` : placeholder}
        </span>
        <DownOutlined
          className={`text-gray-400 text-xs ml-2 flex-shrink-0 transition-transform duration-200 ${
            open ? "rotate-180" : ""
          }`}
        />
      </div>
    </Dropdown>
  );
};

export default CustomCheckboxDropdown;
